import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, useNavigate, useLocation, Routes } from "react-router-dom";
import { Dropdown, Label, Spinner } from "@heroui/react";
import { api } from "./api/client";
import { AiAssistant } from "./components/AiAssistant";
import { useAuth } from "./auth";
import { usePendingPrepCount } from "./hooks/usePendingPrepCount";
import { Prep } from "./pages/Prep";
import { Login } from "./pages/Login";
import { Today } from "./pages/Today";
import { WeekBoard } from "./pages/WeekBoard";
import { RecipeLibrary } from "./pages/RecipeLibrary";
import { ShoppingList } from "./pages/ShoppingList";
import { Improvements } from "./pages/Improvements";
import { RecipeEdit } from "./pages/RecipeEdit";
import { RecipeView } from "./pages/RecipeView";
import { Profile } from "./pages/Profile";
import { AdminUsers } from "./pages/AdminUsers";
import { IngredientsAdmin } from "./pages/IngredientsAdmin";
import { IngredientEdit } from "./pages/IngredientEdit";
import { WeekPrint } from "./pages/WeekPrint";

const NAV_ITEMS: { to: string; label: string; end?: boolean }[] = [
  { to: "/", label: "Avui", end: true },
  { to: "/setmana", label: "Setmana" },
  { to: "/receptes", label: "Receptes" },
  { to: "/ingredients", label: "Ingredients" },
  { to: "/compra", label: "Llista de la compra" },
  { to: "/prep", label: "Preparacions" },
  { to: "/millores", label: "Millores" },
];

/** Pestanyes de la barra inferior mòbil (5 destinacions principals). */
const BOTTOM_NAV_ITEMS: { to: string; label: string; icon: JSX.Element; end?: boolean }[] = [
  { to: "/", label: "Avui", icon: <HomeIcon />, end: true },
  { to: "/setmana", label: "Setmana", icon: <CalendarIcon /> },
  { to: "/receptes", label: "Receptes", icon: <BookIcon /> },
  { to: "/compra", label: "Compra", icon: <CartIcon /> },
  { to: "/perfil", label: "Perfil", icon: <UserIcon /> },
];

/** Campaneta amb comptador de preparacions pendents (per avui o endarrerides). */
function PrepBell({ count }: { count: number }) {
  const nav = useNavigate();

  return (
    <button
      type="button"
      className="prep-bell"
      aria-label={count > 0 ? `${count} preparacions pendents` : "Preparacions"}
      title="Preparacions"
      onClick={() => nav("/prep")}
    >
      <span aria-hidden style={{ fontSize: 18 }}>🔔</span>
      {count > 0 && <span className="prep-bell-badge">{count > 9 ? "9+" : count}</span>}
    </button>
  );
}

export function App() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const prepCount = usePendingPrepCount(!!user);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<{ enabled: boolean }>("/ai/status")
      .then((s) => setAiEnabled(s.enabled))
      .catch(() => setAiEnabled(false));
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!user) return <Login />;

  // Pàgina d'impressió: sense nav ni chrome de l'app
  if (location.pathname === "/setmana/pdf") {
    return (
      <Routes>
        <Route path="/setmana/pdf" element={<WeekPrint />} />
      </Routes>
    );
  }

  async function handleAction(key: string | number) {
    switch (key) {
      case "profile":
        nav("/perfil");
        break;
      case "users":
        nav("/usuaris");
        break;
      case "logout":
        await logout();
        break;
    }
  }

  const navItems = NAV_ITEMS.filter(
    (item) => !(item.to === "/ingredients" && !isAdmin)
  );

  return (
    <div className="flex min-h-screen flex-col">
      <nav className="topnav">
        <div className="flex items-center gap-1.5">
          {/* Marca / títol de l'aplicació */}
          <NavLink to="/" className="brand">
            <img src="/icon.svg" alt="" />
            <b>NaraMenu</b>
          </NavLink>

          {/* Tabs segmentats: només ≥1000px (CSS) */}
          <div className="navtabs">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `navtab ${isActive ? "active" : ""}`}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
        <PrepBell count={prepCount} />
        <Dropdown>
          <Dropdown.Trigger>
            <button type="button" className="account" aria-label="Compte">
              <span className="av" style={{ background: user.avatarColor }} aria-hidden>
                {user.avatarEmoji}
              </span>
              <span>{user.name}</span>
            </button>
          </Dropdown.Trigger>
          <Dropdown.Popover>
            <Dropdown.Menu onAction={handleAction}>
              <Dropdown.Item id="profile" textValue="Edita el perfil">
                <Label>Edita el perfil</Label>
              </Dropdown.Item>
              {isAdmin && (
                <Dropdown.Item id="users" textValue="Gestiona usuaris">
                  <Label>Gestiona usuaris</Label>
                </Dropdown.Item>
              )}
              <Dropdown.Item id="logout" textValue="Sortir" variant="danger">
                <Label>Sortir</Label>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
        </div>
      </nav>

      <main className="main">
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/setmana" element={<WeekBoard />} />
          <Route path="/receptes" element={<RecipeLibrary />} />
          <Route path="/receptes/nova" element={<RecipeEdit />} />
          <Route path="/receptes/:id" element={<RecipeView />} />
          <Route path="/receptes/:id/edita" element={<RecipeEdit />} />
          <Route path="/compra" element={<ShoppingList />} />
          <Route path="/prep" element={<Prep />} />
          <Route path="/millores" element={<Improvements />} />
          <Route path="/perfil" element={<Profile />} />
          {isAdmin && (
            <>
              <Route path="/usuaris" element={<AdminUsers />} />
              <Route path="/ingredients" element={<IngredientsAdmin aiOpen={aiOpen} />} />
              <Route path="/ingredients/nova" element={<IngredientEdit />} />
              <Route path="/ingredients/:id" element={<IngredientEdit />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>

      {/* Barra inferior: només <1000px (CSS), substitueix tota la barra de dalt */}
      <nav className="bottombar">
        {BOTTOM_NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `bottombar-item ${isActive ? "active" : ""}`}
          >
            <span className="bottombar-ic" aria-hidden>
              {item.icon}
              {item.to === "/perfil" && prepCount > 0 && <span className="bottombar-dot" />}
            </span>
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Botó flotant d'assistent IA (cantó inferior dret) */}
      {isAdmin && aiEnabled && !aiOpen && (
        <button
          type="button"
          onClick={() => setAiOpen(true)}
          aria-label="Ajuda IA"
          title="Ajuda IA"
          className="ai-fab flex items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
            <path d="M20 3v4" />
            <path d="M22 5h-4" />
            <path d="M4 17v2" />
            <path d="M5 18H3" />
          </svg>
        </button>
      )}

      {aiOpen && <AiAssistant onClose={() => setAiOpen(false)} />}
    </div>
  );
}

function HomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v9a1 1 0 0 0 1 1H9a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h2.5a1 1 0 0 0 1-1v-9" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21.5V5.5Z" />
      <path d="M20 19H6.5A2.5 2.5 0 0 0 4 21.5" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 4h2l2.4 12.4a2 2 0 0 0 2 1.6h7.2a2 2 0 0 0 2-1.6L20.5 8H6" />
      <circle cx="9.5" cy="20.5" r="1.4" />
      <circle cx="17" cy="20.5" r="1.4" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20.5c1.4-4 4-6 7.5-6s6.1 2 7.5 6" />
    </svg>
  );
}
